import {
  APIFieldAttributes,
  APIDiscoverViewpointAttributes,
  Direction,
  EntryPoint,
  NonPersistedDiscoverViewpoint,
  Triple,
  TypeRelationship,
  AssetType,
  ViewIds,
  GroupType,
  APIComponentAttributes,
  TraversalPathMatchingType,
  APIViewpointAttributes,
  APICurrentUser,
  ArdoqId,
} from '@ardoq/api-types';
import { isTruthy } from '@ardoq/common-helpers';
import { localeCompare, type Locale } from '@ardoq/locale';
import { isEqual, keyBy, uniq, uniqWith } from 'lodash';
import partition from 'lodash/partition';
import { v4 as uuidv4 } from 'uuid';
import {
  FieldSelectOption,
  PartialConstraint,
  PartialStep,
  PartialTriple,
  Step,
  SyntheticViewpointPayload as SyntheticViewpoint,
  ViewPointWithFavoriteFlag,
} from './types';
import { dateRangeOperations, isDateRangeFieldType } from '@ardoq/date-range';
import { SelectOption } from '@ardoq/select';
import { getReferenceOptionLeftContent } from './ReferenceOptionLeftContent';
import { getFavorites } from 'streams/currentUser/currentUser$';
import { getDefaultWildcardViewpoint } from 'traverseOneLevel/defaultWildcardViewpoint';
import { Features, hasFeature } from '@ardoq/features';
import { ExecuteUnsavedViewpointPayload } from 'viewpointBuilder/actions';
import { ComponentIdAndTypeName } from '@ardoq/data-model';
import { anyType } from '@ardoq/global-consts';
import { getDefaultOverviewName } from './defaultOverviewName';
import { type ReferenceTypesByWorkspaceId } from 'streams/models/referenceTypesByWorkspaceId$';
import { type ComponentTypesByWorkspaceId } from 'streams/models/componentTypesByWorkspaceId$';
import { type FieldsByWorkspaceId } from 'streams/fields/fieldsByWorkspaceId$';
import { type TraversalState } from 'streams/traversals/types';
import { type I18nLocale } from '@ardoq/i18n';
import { getDefaultOverviewId } from './defaultOverview';
import { getDefaultViewpoint } from './getDefaultViewpoint';

const isFavorite = (
  viewpointId: string,
  currentUser: APICurrentUser
): boolean => {
  const favorites = getFavorites(currentUser);
  const favoriteTraversalIds = favorites[AssetType.TRAVERSAL];
  return favoriteTraversalIds.includes(viewpointId);
};

export function isPersisted(
  viewpoint: APIDiscoverViewpointAttributes | NonPersistedDiscoverViewpoint
): viewpoint is APIDiscoverViewpointAttributes {
  return '_id' in viewpoint;
}

const getComponentTypeNamesInStep = ({ constraints }: Step) => {
  return uniq(
    constraints.flatMap(({ triple }) => [triple[0], triple[2]]).filter(isTruthy)
  );
};

const getReferenceTypeNamesInStep = ({ constraints }: Step) => {
  return uniq(
    constraints.flatMap(({ triple }) => [triple[1]]).filter(isTruthy)
  );
};

export const getStepsThatIncludeComponentTypeName = (
  steps: Step[],
  componentTypeName: string
) => {
  return steps.filter(step => {
    return getComponentTypeNamesInStep(step).includes(componentTypeName);
  });
};

export const getSelectedComponentTypeNames = (steps: Step[]) => {
  return uniq(steps.flatMap(getComponentTypeNamesInStep));
};

export const getSelectedReferenceTypeNames = (steps: Step[]) => {
  return uniq(steps.flatMap(getReferenceTypeNamesInStep));
};

const getExistingStepIdForComponentTypeName = (
  entryPoints: EntryPoint[],
  componentTypeName: string
) => {
  const entryPoint = entryPoints.find(
    ([entryPointComponentTypeName]) =>
      entryPointComponentTypeName === componentTypeName
  );
  if (!entryPoint) return null;
  return entryPoint[1];
};

const deriveStepId = (
  currentStepId: string | null,
  newSteps: Step[],
  componentTypeName: string
) => {
  const stepsThatIncludeComponentTypeName =
    getStepsThatIncludeComponentTypeName(newSteps, componentTypeName);
  if (stepsThatIncludeComponentTypeName.length === 0) return null;
  const newStepId = stepsThatIncludeComponentTypeName.some(
    ({ id }) => id === currentStepId
  )
    ? currentStepId
    : stepsThatIncludeComponentTypeName[0].id;
  return newStepId;
};

const deriveNewEntryPoint =
  (currentEntryPoints: EntryPoint[], newSteps: Step[]) =>
  (componentTypeName: string) => {
    const currentStepId = getExistingStepIdForComponentTypeName(
      currentEntryPoints,
      componentTypeName
    );
    const newStepId = deriveStepId(currentStepId, newSteps, componentTypeName);
    if (!newStepId) return null;
    return [componentTypeName, newStepId];
  };

export const deriveNewEntryPoints = (
  currentEntryPoints: EntryPoint[],
  newSteps: Step[]
) => {
  return getSelectedComponentTypeNames(newSteps)
    .map(deriveNewEntryPoint(currentEntryPoints, newSteps))
    .filter(isTruthy);
};

export const getAvailableTriples = (typeRelationships: TypeRelationship[]) => {
  const availableTriples = typeRelationships.map(
    ({ sourceType, referenceType, targetType }): Triple =>
      [sourceType, referenceType, targetType] as Triple
  );
  return uniqWith(availableTriples, isEqual);
};

const isSupersetOf =
  (subsetTriple: PartialTriple) => (supersetTriple: Triple) => {
    return supersetTriple.every((typeName, index) => {
      return [typeName, null].includes(subsetTriple[index]);
    });
  };

export const hasValidTriple = (
  { triple }: PartialConstraint,
  availableTriples: Triple[]
) => {
  return availableTriples.some(isSupersetOf(triple));
};

export const withoutInvalidTriples = (
  currentSteps: PartialStep[],
  availableTriples: Triple[]
): PartialStep[] => {
  const newSteps = currentSteps.map(step => ({
    ...step,
    constraints: step.constraints.filter(constraint =>
      hasValidTriple(constraint, availableTriples)
    ),
  }));
  return newSteps;
};

export const entryPointIsAmbiguous = (
  steps: Step[],
  entryPoint: EntryPoint
) => {
  const componentTypeName = entryPoint[0];
  const stepsThatIncludeComponentTypeName =
    getStepsThatIncludeComponentTypeName(steps, componentTypeName);
  return stepsThatIncludeComponentTypeName.length > 1;
};

const getAmbiguousEntryPoints = (steps: Step[], entryPoints: EntryPoint[]) => {
  return entryPoints.filter(entryPoint =>
    entryPointIsAmbiguous(steps, entryPoint)
  );
};

export const hasAmbiguousEntryPoints = (
  steps: Step[],
  entryPoints: EntryPoint[]
) => {
  return getAmbiguousEntryPoints(steps, entryPoints).length > 0;
};

const getFromType = ({ triple, direction }: PartialConstraint) => {
  switch (direction) {
    case 'outgoing':
      return triple[0];
    case 'incoming':
      return triple[2];
    case 'undirected':
      return [triple[0], triple[2]];
  }
};
const getFromTypes = (constraints: PartialConstraint[]) => {
  return new Set(constraints.flatMap(getFromType).filter(isTruthy));
};

const getToType = ({ triple, direction }: PartialConstraint) => {
  switch (direction) {
    case 'outgoing':
      return triple[2];
    case 'incoming':
      return triple[0];
    case 'undirected':
      return [triple[0], triple[2]];
  }
};
const getToTypes = (constraints: PartialConstraint[]) => {
  return new Set(constraints.flatMap(getToType).filter(isTruthy));
};

export const startSetEqualsEndSet = (constraints: PartialConstraint[]) => {
  const fromTypes = getFromTypes(constraints);
  const toTypes = getToTypes(constraints);
  return fromTypes.size > 0 && isEqual(fromTypes, toTypes);
};

export const createEmptyConstraint = (
  creationContext: 'triple' | 'step',
  availableTriples: Triple[],
  previousStep?: PartialStep
): PartialConstraint => {
  if (!previousStep?.constraints.length) {
    return {
      triple: [null, null, null],
      direction: 'outgoing',
    };
  }
  const lastConstraint =
    previousStep.constraints[previousStep.constraints.length - 1];
  let relevantComponentTypeIndex =
    lastConstraint.direction === 'outgoing' ? 2 : 0;

  if (creationContext === 'triple') {
    relevantComponentTypeIndex =
      lastConstraint.direction === 'outgoing' ? 0 : 2;
  }

  if (lastConstraint.triple[relevantComponentTypeIndex] === null) {
    return {
      triple: [null, null, null],
      direction: 'outgoing',
    };
  }

  return getConstraintWithUpdatedStartType(
    availableTriples,
    lastConstraint.triple[relevantComponentTypeIndex] ?? ''
  );
};

export const createEmptyStep = (
  previousStep: PartialStep,
  availableTriples: Triple[]
) => ({
  id: uuidv4(),
  constraints: [createEmptyConstraint('step', availableTriples, previousStep)],
  minRepetition: 0,
  maxRepetition: 1,
});

export const getPossibleStartTypeNames = (availableTriples: Triple[]) => {
  return uniq(availableTriples.flatMap(triple => [triple[0], triple[2]]));
};

export const getStartTypeOptions = (
  availableTriples: Triple[],
  currentConstraint: PartialConstraint,
  isDisabled: boolean
): (SelectOption<string> & { constraint: PartialConstraint })[] => {
  const possibleOptions = getPossibleStartTypeNames(availableTriples).map(
    startTypeName => ({
      value: startTypeName,
      label: startTypeName,
      constraint: getConstraintWithUpdatedStartType(
        availableTriples,
        startTypeName
      ),
    })
  );

  // I can't figure out a way to do it other than this code smell.
  const currentFirstTypeName = getStartTypeName(currentConstraint);
  if (
    !currentFirstTypeName ||
    possibleOptions.some(option => option.value === currentFirstTypeName)
  ) {
    return possibleOptions;
  }

  return [
    ...possibleOptions,
    {
      value: currentFirstTypeName,
      label: currentFirstTypeName,
      isDisabled,
      constraint: getConstraintWithUpdatedStartType(
        [currentConstraint.triple as Triple],
        currentFirstTypeName
      ),
    },
  ];
};

export const getPossibleReferenceTypes = (
  availableTriples: Triple[],
  constraint: PartialConstraint
) => {
  const currentStartTypeName = getStartTypeName(constraint);
  return uniqWith(
    availableTriples
      .flatMap(triple => {
        const outgoingReference =
          triple[0] === currentStartTypeName
            ? { typeName: triple[1], direction: 'outgoing' as Direction }
            : null;
        const incomingReference =
          triple[2] === currentStartTypeName
            ? { typeName: triple[1], direction: 'incoming' as Direction }
            : null;
        return [outgoingReference, incomingReference];
      })
      .filter(isTruthy),
    isEqual
  );
};

export const getReferenceOptions = (
  availableTriples: Triple[],
  constraint: PartialConstraint,
  isDisabled: boolean
): (SelectOption<string | null> & { constraint: PartialConstraint })[] => {
  const possibleOptions = getPossibleReferenceTypes(
    availableTriples,
    constraint
  ).map(({ typeName, direction }) => {
    return {
      value: getReferenceTypeOptionValue(typeName, direction),
      label: '',
      constraint: getConstraintWithUpdatedReferenceType(
        constraint,
        typeName,
        direction
      ),
      ...getReferenceOptionLeftContent(direction, typeName),
    };
  });

  // I can't figure out a way to do it other than this code smell.
  const currentReferenceName = getReferenceTypeOptionValue(
    constraint.triple[1],
    constraint.direction
  );
  if (
    !currentReferenceName ||
    possibleOptions.some(option => option.value === currentReferenceName)
  ) {
    return possibleOptions;
  }

  return [
    ...possibleOptions,
    {
      value: currentReferenceName,
      label: '',
      isDisabled,
      constraint: getConstraintWithUpdatedReferenceType(
        constraint,
        constraint.triple[1]!,
        constraint.direction
      ),
      ...getReferenceOptionLeftContent(
        constraint.direction,
        constraint.triple[1]!
      ),
    },
  ];
};

export const getPossibleEndTypeNames = (
  availableTriples: Triple[],
  constraint: PartialConstraint
) => {
  const startTypeIndex = constraint.direction === 'outgoing' ? 0 : 2;
  const endTypeIndex = constraint.direction === 'outgoing' ? 2 : 0;
  return uniq(
    availableTriples
      .filter(triple => {
        return (
          triple[1] === constraint.triple[1] &&
          triple[startTypeIndex] === constraint.triple[startTypeIndex]
        );
      })
      .map(triple => triple[endTypeIndex])
  );
};

export const getEndTypeOptions = (
  availableTriples: Triple[],
  constraint: PartialConstraint,
  isDisabled: boolean
): (SelectOption<string> & { constraint: PartialConstraint })[] => {
  const possibleOptions = getPossibleEndTypeNames(
    availableTriples,
    constraint
  ).map(endTypeName => {
    return {
      value: endTypeName,
      label: endTypeName,
      constraint: getConstraintWithUpdatedEndType(constraint, endTypeName),
    };
  });

  // I can't figure out a way to do it other than this code smell.
  const currentEndTypeName = getEndTypeName(constraint);
  if (
    !currentEndTypeName ||
    possibleOptions.some(option => option.value === currentEndTypeName)
  ) {
    return possibleOptions;
  }

  return [
    ...possibleOptions,
    {
      label: currentEndTypeName,
      value: currentEndTypeName,
      isDisabled,
      constraint: getConstraintWithUpdatedEndType(
        constraint,
        currentEndTypeName
      ),
    },
  ];
};

const getStartTypeName = (constraint: PartialConstraint) => {
  return constraint.direction === 'outgoing'
    ? constraint.triple[0]
    : constraint.triple[2];
};

const getEndTypeName = (constraint: PartialConstraint) => {
  return constraint.direction === 'outgoing'
    ? constraint.triple[2]
    : constraint.triple[0];
};

const isSourceType = (availableTriples: Triple[], typeName: string) => {
  return availableTriples.some(([sourceTypeName]) => {
    return sourceTypeName === typeName;
  });
};

const getConstraintWithUpdatedStartType = (
  availableTriples: Triple[],
  startTypeName: string
): PartialConstraint => {
  return isSourceType(availableTriples, startTypeName)
    ? { triple: [startTypeName, null, null], direction: 'outgoing' }
    : { triple: [null, null, startTypeName], direction: 'incoming' };
};

const getConstraintWithUpdatedReferenceType = (
  constraint: PartialConstraint,
  newReferenceTypeName: string,
  newDirection: Direction
): PartialConstraint => {
  const currentStartTypeName = getStartTypeName(constraint);
  return {
    triple:
      newDirection === 'outgoing'
        ? [currentStartTypeName, newReferenceTypeName, null]
        : [null, newReferenceTypeName, currentStartTypeName],
    direction: newDirection,
  };
};

const getConstraintWithUpdatedEndType = (
  constraint: PartialConstraint,
  newEndTypeName: string
) => {
  return {
    ...constraint,
    triple: (constraint.direction === 'outgoing'
      ? [constraint.triple[0], constraint.triple[1], newEndTypeName]
      : [newEndTypeName, constraint.triple[1], constraint.triple[2]]) as Triple,
  };
};

export const getReferenceTypeOptionValue = (
  referenceTypeName: string | null,
  direction: Direction
) => {
  if (referenceTypeName === null) return null;
  return `${direction}${referenceTypeName}`;
};

export const getTripleInTraversalOrder = (constraint: PartialConstraint) => {
  return constraint.direction === 'outgoing'
    ? constraint.triple
    : [...constraint.triple].reverse();
};

export const toFieldSelectOption = ({
  label,
  name,
  type,
}: APIFieldAttributes): FieldSelectOption => ({
  label,
  value: name,
  category: 'field',
  isDateRange: isDateRangeFieldType(type),
});

export const getReferenceCustomFieldOptions = (
  workspaceIds: string[],
  referenceTypeName: string,
  referenceTypesByWorkspaceId: ReferenceTypesByWorkspaceId,
  fieldsByWorkspaceId: FieldsByWorkspaceId
): FieldSelectOption[] => {
  const workspaceIdsWithReferenceType = workspaceIds.filter(workspaceId => {
    return (referenceTypesByWorkspaceId[workspaceId] ?? []).some(
      ({ name }) => name === referenceTypeName
    );
  });
  const referenceTypeIds = uniq(
    workspaceIdsWithReferenceType.flatMap(workspaceId => {
      return (referenceTypesByWorkspaceId[workspaceId] ?? [])
        .filter(({ name }) => name === referenceTypeName)
        .map(({ id }) => id);
    })
  );
  const fields = workspaceIdsWithReferenceType.flatMap(workspaceId => {
    return (fieldsByWorkspaceId[workspaceId] ?? []).filter(field => {
      return (
        field.global ||
        referenceTypeIds.some(referenceTypeId =>
          field.referenceType.includes(`${referenceTypeId}`)
        )
      );
    });
  });
  return dateRangeOperations
    .mergeDateTimeFieldsToDateRangeFields(fields)
    .fields.map(toFieldSelectOption);
};

export const getComponentCustomFieldOptions = (
  workspaceIds: string[],
  componentTypeName: string,
  componentTypesByWorkspaceId: ComponentTypesByWorkspaceId,
  fieldsByWorkspaceId: FieldsByWorkspaceId
): FieldSelectOption[] => {
  const workspaceIdsWithComponentType = workspaceIds.filter(workspaceId => {
    return (componentTypesByWorkspaceId[workspaceId] ?? []).some(
      ({ name }) => name === componentTypeName
    );
  });
  const componentTypeIds = uniq(
    workspaceIdsWithComponentType.flatMap(workspaceId => {
      return (componentTypesByWorkspaceId[workspaceId] ?? [])
        .filter(({ name }) => name === componentTypeName)
        .map(({ id }) => id);
    })
  );
  const fields = workspaceIdsWithComponentType.flatMap(workspaceId => {
    return (fieldsByWorkspaceId[workspaceId] ?? []).filter(field => {
      return (
        field.global ||
        componentTypeIds.some(componentTypeId =>
          field.componentType.includes(componentTypeId)
        )
      );
    });
  });
  return dateRangeOperations
    .mergeDateTimeFieldsToDateRangeFields(fields)
    .fields.map(toFieldSelectOption);
};

export const updateViewpointInViewpointListById = (
  viewpoints: APIDiscoverViewpointAttributes[],
  newViewpoint: APIDiscoverViewpointAttributes
) => {
  const viewPointIndex = viewpoints.findIndex(
    viewpoint => viewpoint._id === newViewpoint._id
  );

  if (viewPointIndex === -1) {
    return [...viewpoints, newViewpoint];
  }
  const newViewpoints = [...viewpoints];
  newViewpoints[viewPointIndex] = newViewpoint;
  return newViewpoints;
};

export const updateCurrentViewpointIfNeeded = (
  currentViewpoint: APIDiscoverViewpointAttributes | null,
  newViewpoint: APIDiscoverViewpointAttributes
) =>
  currentViewpoint && currentViewpoint._id === newViewpoint._id
    ? newViewpoint
    : currentViewpoint;

export const prepareIsIncluded =
  <T>(list: T[]) =>
  (value: T) =>
    list.includes(value);

export const createOverviewViewpointForSingleComponent = (
  component: Pick<APIComponentAttributes, '_id' | 'name' | 'type'>,
  i18nLocale: I18nLocale
): SyntheticViewpoint => {
  return {
    isOverview: true,
    name: getDefaultOverviewName(component.type, i18nLocale),
    description: `See everything directly connected to ${component.name}`,
    viewName: ViewIds.MODERNIZED_BLOCK_DIAGRAM,
    ...createOverviewViewpointForMultipleComponents([
      { id: component._id, typeName: component.type },
    ]),
  };
};

export const createOverviewViewpointForMultipleComponents = (
  components: ComponentIdAndTypeName[]
): ExecuteUnsavedViewpointPayload => {
  const typeNames = uniq(components.map(c => c.typeName));
  const componentIds = components.map(c => c.id);
  const startTypeName = typeNames.length === 1 ? typeNames[0] : anyType;
  return {
    type: 'EXECUTE_UNSAVED_VIEWPOINT',
    viewName: ViewIds.MODERNIZED_BLOCK_DIAGRAM,
    paths: getDefaultWildcardViewpoint(startTypeName),
    filters: {},
    pathMatching: TraversalPathMatchingType.LOOSE,
    pathCollapsingRules: [],
    componentSelection: {
      startSet: componentIds,
    },
    groupBys: [
      {
        type: GroupType.WORKSPACE,
      },
      {
        type: GroupType.PARENT_ALL,
      },
    ],
    conditionalFormatting: [],
  };
};

export const byNameAscending =
  (locale: Locale) =>
  (a: APIViewpointAttributes, b: APIViewpointAttributes): number =>
    localeCompare(a.name.toLowerCase(), b.name.toLowerCase(), locale);

export const byPriorityOrder = (priorityViewpointIds: ArdoqId[]) => {
  const orderMap = new Map(
    priorityViewpointIds.map((id, index) => [id, index])
  );
  return (
    a: { attributes: { _id: ArdoqId } },
    b: { attributes: { _id: ArdoqId } }
  ): number => {
    const aIndex = orderMap.get(a.attributes._id) ?? Infinity;
    const bIndex = orderMap.get(b.attributes._id) ?? Infinity;
    if (aIndex === bIndex) return 0;
    return aIndex - bIndex;
  };
};

type GetAllPriorityViewpointsArgs = {
  component: Pick<APIComponentAttributes, 'type'>;
  viewpoints: APIViewpointAttributes[];
  priorityViewpointIds: ArdoqId[];
  defaultOverviewDescription: string;
  i18nLocale: I18nLocale;
};

export const getAllPriorityViewpoints = ({
  component,
  viewpoints,
  priorityViewpointIds,
  defaultOverviewDescription,
  i18nLocale,
}: GetAllPriorityViewpointsArgs): APIViewpointAttributes[] => {
  const viewpointById = keyBy(viewpoints, '_id');
  const defaultOverviewId = getDefaultOverviewId(component.type);
  return priorityViewpointIds
    .map(id =>
      id === defaultOverviewId
        ? getDefaultViewpoint({
            startType: component.type,
            description: defaultOverviewDescription,
            i18nLocale,
          })
        : viewpointById[id]
    )
    .filter(
      (viewpoint): viewpoint is APIViewpointAttributes =>
        viewpoint !== undefined && viewpoint.startType === component.type
    );
};

type GetFirstPriorityViewpointIdArgs = {
  componentTypeName: string;
  traversals: TraversalState;
  viewpointIds: ArdoqId[];
};

export const getFirstPriorityViewpointId = ({
  componentTypeName,
  traversals,
  viewpointIds,
}: GetFirstPriorityViewpointIdArgs): ArdoqId | null => {
  const viewpointIdsForType = new Set([
    getDefaultOverviewId(componentTypeName),
    ...traversals.list
      .filter(viewpoint => viewpoint.startType === componentTypeName)
      .map(viewpoint => viewpoint._id),
  ]);
  return viewpointIds.find(id => viewpointIdsForType.has(id)) ?? null;
};

export const getRelevantViewpoints = (
  viewpoints: APIViewpointAttributes[],
  component: APIComponentAttributes,
  currentUser: APICurrentUser
): ViewPointWithFavoriteFlag[] => {
  const matchingViewpoints = viewpoints.filter(
    viewpoint => viewpoint.startType === component.type
  );
  const [favoriteViewpoints, nonFavoriteViewpoints] = partition(
    matchingViewpoints,
    (v: APIViewpointAttributes) => isFavorite(v._id, currentUser)
  );

  return [
    ...favoriteViewpoints.map(viewpoint => ({ ...viewpoint, favorite: true })),
    ...nonFavoriteViewpoints,
  ];
};

export const getRelevantViewpointsWithOverview = (
  viewpoints: APIViewpointAttributes[],
  component: APIComponentAttributes,
  currentUser: APICurrentUser,
  overviewViewpoint: SyntheticViewpoint
): (ViewPointWithFavoriteFlag | SyntheticViewpoint)[] => {
  const matchingViewpoints = viewpoints.filter(
    viewpoint => viewpoint.startType === component.type
  );
  const [favoriteViewpoints, nonFavoriteViewpoints] = partition(
    matchingViewpoints,
    (v: APIViewpointAttributes) => isFavorite(v._id, currentUser)
  );

  const overview = hasFeature(
    Features.DISCOVER_CORE_LANDING_PAGE_AND_NAVIGATION
  )
    ? [overviewViewpoint]
    : [];

  return [
    ...favoriteViewpoints.map(viewpoint => ({ ...viewpoint, favorite: true })),
    ...overview,
    ...nonFavoriteViewpoints,
  ];
};
